import {
  Actions,
  AppArea,
  CreateMostFavouriteDocument,
  MostFavouriteByJobConfigExactMatchDocument,
  MostFavouriteByJobConfigExactMatchQuery,
  Subjects,
  UpdateMostFavouriteDocument,
} from 'src/graphql/generated/graphql';
import { cloneElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentNode, useMutation, useQuery } from '@apollo/client';
import { Star } from 'src/components/common/icons';
import { ActionConfigForm } from '../list/scheduled-jobs/form/action-config';
import { Form, MostScheduledJob } from '../list/scheduled-jobs/form/types';
import { getJob } from '../list/scheduled-jobs/form/scheduled-job';
import { Schedule } from '@otp/icons';
import { Typography } from '@otp/ui';
import { Can } from 'src/utils/can';

type AddToFavoritesProps = {
  id?: string;
  defaultValue?: Partial<MostScheduledJob> | undefined;
  refetchQueries?: DocumentNode[];
  checkIfIsFavourited?: boolean;
  title?:
    | string
    | (string & React.ReactPortal)
    | (string &
        React.ReactElement<any, string | React.JSXElementConstructor<any>>)
    | (string & Iterable<React.ReactNode>);
  children?: React.ReactElement;
  onFinish?: (id?: string) => void;
};

export const AddToFavorites = ({
  id,
  defaultValue,
  refetchQueries,
  checkIfIsFavourited = false,
  title,
  onFinish,
  children,
  ...rest
}: AddToFavoritesProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'most.list.favourite.form',
  });

  const [showFavoritesModal, setShowFavoritesModal] = useState(false);

  const { data: isFavouritedData, refetch } =
    useQuery<MostFavouriteByJobConfigExactMatchQuery>(
      MostFavouriteByJobConfigExactMatchDocument,
      {
        variables: {
          filter: {
            commandId: defaultValue?.jobConfig?.command.commandId || '',
            type: defaultValue?.jobConfig?.command.type,
            devices: defaultValue?.jobConfig?.devices || [],
            storeIds: defaultValue?.jobConfig?.storeIds || [],
          },
        },
        skip: !defaultValue || !checkIfIsFavourited,
      },
    );

  const isFavourited = !!isFavouritedData?.mostFavouriteByJobConfigExactMatch;

  const [createEntity, { loading: loadingCreate }] = useMutation(
    CreateMostFavouriteDocument,
    {
      refetchQueries,
    },
  );
  const [updateEntity, { loading: loadingUpdate }] = useMutation(
    UpdateMostFavouriteDocument,
    {
      refetchQueries,
    },
  );

  const loading = loadingCreate || loadingUpdate;

  const onSubmit = async (form: Form) => {
    const data = {
      jobConfig: getJob(form),
    };

    if (!id) {
      const result = await createEntity({
        variables: { data },
      });
      onFinish?.(result.data?.createMostFavourite.id);
    } else {
      const result = await updateEntity({
        variables: {
          data,
          id: id,
        },
      });
      onFinish?.(result.data?.updateMostFavourite.id);
    }

    refetch();
    setShowFavoritesModal(false);
  };

  const onClick = () => {
    setShowFavoritesModal(true);
  };
  const renderChildren = (children: React.ReactElement) =>
    cloneElement(children, { onClick });

  let renderedChildren = (
    <Star
      isActive={isFavourited || false}
      onClick={onClick}
      data-role={isFavourited ? 'selected-row' : ''}
    />
  );

  if (children) {
    renderedChildren = renderChildren(children);
  }

  const titleElement =
    title ||
    ((
      <div className="flex">
        <Schedule className="mr-2" />
        <Typography variant="subtitle1">{t('add-to-favourites')}</Typography>
      </div>
    ) as any);

  return (
    <Can I={Actions.Write} a={Subjects.Favourites} appArea={AppArea.Most}>
      {renderedChildren}
      {showFavoritesModal && (
        <ActionConfigForm
          onClose={() => setShowFavoritesModal(false)}
          defaultValues={defaultValue}
          onSubmit={onSubmit}
          loading={loading}
          submitButtonLabel={t(id ? 'save' : 'submit')}
          hiddenFields={['schedule', 'recurring', 'callout']}
          title={titleElement}
          showDetails={true}
          {...rest}
        />
      )}
    </Can>
  );
};
